import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { Button } from "../components/ui/button";
import { Section } from "../components/ui/section";
import { Card, CardContent } from "../components/ui/card";
import HeroInvestmentPanel from "../components/HeroInvestmentPanel";
import PricingSection from "../components/PricingSection";
import PropertyCarousel from "../components/PropertyCarousel";
import { useGalleryProperties } from "../hooks/useProperties";
import { plans } from "../data/plans";
import { testimonials } from "../data/testimonials";
import { FeatureCard } from "@/components/main/FeatureCard";
import { solutions } from "@/utils/useSolutions";

export default function LandingPage() {
  const { properties, loading, error } = useGalleryProperties();
  const featuredProperties = properties.slice(0, 6);

  return (
    <div className="min-h-screen [background:var(--page-gradient)]">
      {/* Hero Section */}
      <HeroInvestmentPanel />

      {/* Features Section */}
      <section id="solutions" className="py-20 bg-secondary/30">
        <div className="container mx-auto px-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Your Unfair Advantage
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything the big hotel groups have — minus the bureaucracy and
              seven-figure consulting fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <FeatureCard
                key={index}
                icon={solution.icon}
                title={solution.title}
                description={solution.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Property Gallery Preview */}
      <Section className="bg-gradient-to-b from-slate-50 to-white py-16">
        {error && (
          <div className="text-center text-red-600 mb-4">
            <p>Failed to load properties: {error}</p>
          </div>
        )}
        <PropertyCarousel properties={featuredProperties} loading={loading} />
        <div className="text-center">
          <Link to="/properties">
            <Button
              variant="outline"
              className="flex items-center space-x-2 mx-auto transform hover:scale-105 transition-all duration-300 bg-[hsl(215_85%_45%)] border-[hsl(215_85%_45%)] text-white"
            >
              <span>See all properties</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* Pricing Section */}
      <Section className="bg-white">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            Choose your plan
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto font-medium leading-relaxed">
            From beta access to full consultancy support—find the right plan for
            your investment needs.
          </p>
        </div>
        <PricingSection plans={plans} />
        <div className="text-center mt-8">
          <Link to="/membership">
            <Button
              variant="outline"
              className="flex items-center space-x-2 mx-auto transform transition-all duration-300 bg-[hsl(215_85%_45%)] border-[hsl(215_85%_45%)] text-white"
            >
              <span>View detailed comparison</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </Section>

      {/* Testimonials Section */}
      <Section className="bg-gradient-to-b from-slate-50 to-white">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-black text-slate-900 mb-6 tracking-tight">
            Trusted by hotel investors
          </h2>
          <p className="text-xl text-slate-600 font-medium">
            See what industry professionals say about Hotel Foundry.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              className="bg-card-gradient backdrop-blur-sm border-white/50 shadow-card"
            >
              <CardContent className="p-8">
                <Quote className="h-8 w-8 text-primary-blue mb-4" />
                <p className="text-slate-700 leading-relaxed mb-6 font-medium">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-navy-200 pt-4">
                  <p className="font-bold text-slate-900">
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-slate-600 font-medium">
                    {testimonial.role}
                  </p>
                  {testimonial.company && (
                    <p className="text-sm text-slate-500">
                      {testimonial.company}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </Section>

      {/* CTA Section */}
      <Section className="bg-electric-gradient text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 via-transparent to-accent-500/20"></div>
        <div className="text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-6 tracking-tight relative z-10">
            Ready to underwrite with confidence?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto font-medium leading-relaxed relative z-10">
            Join the platform that's transforming hotel investment analysis
            across European leisure markets.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center relative z-10">
            <Link to="/membership">
              <Button
                size="lg"
                className="bg-[hsl(215_85%_45%)] hover:bg-[hsl(215_85%_38%)] active:bg-[hsl(215_85%_35%)] text-white shadow-xl hover:shadow-2xl active:shadow-lg transform hover:scale-105 active:scale-95 transition-all duration-300 font-bold px-8 border-[hsl(215_85%_45%)] hover:border-[hsl(215_85%_38%)]"
              >
                Get Started Today
              </Button>
            </Link>
            <Link to="/properties">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white/60 bg-transparent text-white hover:bg-[hsl(215_85%_45%)] hover:text-white hover:border-[hsl(215_85%_45%)] active:bg-[hsl(215_85%_38%)] active:border-[hsl(215_85%_38%)] hover:shadow-xl active:shadow-lg transform hover:scale-105 active:scale-95 transition-all duration-300 font-semibold px-8"
              >
                Browse Properties
              </Button>
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
